import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { formatTimeWindow } from '../utils/timeWindows';
import {
  buildDayRange,
  formatChipMetaLine,
  formatRouteLabel,
  getRouteStyle,
  getSeatsRemaining,
  groupTripsByDate,
  layoutOverlappingTrips,
  TIMELINE_LABELS,
  toDateKey,
} from '../utils/tripSchedule';

const TIMELINE_HEIGHT = 320;
const COMPACT_TIMELINE_HEIGHT = 200;

function DaySummary({ trips }) {
  return (
    <div className="px-3 py-2 border-b border-stone-200/80 bg-stone-50/80">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-2xl font-serif font-bold text-[#2D1E17]">{trips.length}</span>
        <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
          {trips.length === 1 ? 'trip' : 'trips'}
        </span>
      </div>
    </div>
  );
}

function TripChip({
  trip,
  layout,
  compact,
  isSelected,
  isJoined,
  onSelect,
}) {
  const routeStyle = getRouteStyle(trip.pickup_location);
  const timeLabel = formatTimeWindow(trip.time_window_start, trip.time_window_end);
  const seatsLeft = getSeatsRemaining(trip);
  const routeLabel = formatRouteLabel(trip, { compact });
  const metaLine = formatChipMetaLine(trip, { seatsLeft, isJoined });

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(trip);
      }}
      className={`absolute rounded-lg border text-left text-white shadow-sm transition-all hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#A44432] focus:ring-offset-1 overflow-hidden ${routeStyle.chip} ${
        isSelected ? 'ring-2 ring-white z-30 brightness-110' : 'hover:z-20'
      } ${isJoined ? 'ring-2 ring-white/80' : ''}`}
      style={{
        top: layout.topPx,
        height: layout.heightPx,
        minHeight: compact ? 26 : 38,
        left: `${layout.leftPct}%`,
        width: `${layout.widthPct}%`,
      }}
      title={`${trip.pickup_location} → ${trip.dropoff_location} · ${timeLabel} · ${seatsLeft} seat${seatsLeft !== 1 ? 's' : ''} left${isJoined ? ' · Joined' : ''}`}
    >
      <div className={`h-full flex flex-col justify-center gap-0.5 px-1 ${compact ? 'py-0.5' : 'py-1'}`}>
        <span
          className={`font-semibold leading-tight truncate ${compact ? 'text-[8px]' : 'text-[10px]'}`}
          title={routeLabel}
        >
          {routeLabel}
        </span>
        <span className={`opacity-90 truncate ${compact ? 'text-[7px]' : 'text-[9px]'}`}>
          {metaLine}
        </span>
      </div>
    </button>
  );
}

function DayColumn({
  date,
  trips,
  compact,
  selectedTripId,
  onSelectTrip,
  isUserJoined,
}) {
  const dateKey = toDateKey(date);
  const isToday = dateKey === toDateKey(new Date());
  const timelineHeight = compact ? COMPACT_TIMELINE_HEIGHT : TIMELINE_HEIGHT;
  const chipLayouts = layoutOverlappingTrips(trips, timelineHeight);

  return (
    <div
      className={`flex flex-col min-w-0 rounded-xl border overflow-hidden bg-white transition-shadow ${
        isToday ? 'border-[#A44432] ring-1 ring-[#A44432]/30 shadow-md' : 'border-stone-200 shadow-sm'
      }`}
    >
      <div className={`px-3 py-2.5 border-b border-stone-200 ${isToday ? 'bg-[#2D1E17] text-white' : 'bg-white'}`}>
        <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-[#FAF3E0]/80' : 'text-stone-500'}`}>
          {format(date, 'EEE')}
        </p>
        <p className={`text-sm font-bold ${isToday ? 'text-white' : 'text-[#2D1E17]'}`}>
          {format(date, 'd MMM')}
        </p>
      </div>

      <DaySummary trips={trips} />

      <div className="relative flex-1 min-h-0 p-1">
        <div className="relative ml-7 mr-0" style={{ height: timelineHeight }}>
          {TIMELINE_LABELS.map(({ label, minutes }) => (
            <div
              key={label}
              className="absolute left-0 right-0 border-t border-stone-100 pointer-events-none"
              style={{ top: `${(minutes / (24 * 60)) * 100}%` }}
            >
              <span className="absolute -left-7 -translate-y-1/2 text-[9px] text-stone-400 font-medium w-6 text-right">
                {label}
              </span>
            </div>
          ))}

          {chipLayouts.map((layout) => (
            <TripChip
              key={layout.trip.id}
              trip={layout.trip}
              layout={layout}
              compact={compact}
              isSelected={selectedTripId === layout.trip.id}
              isJoined={isUserJoined?.(layout.trip)}
              onSelect={onSelectTrip}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const TripScheduleView = ({
  trips,
  dayCount = 7,
  selectedTripId = null,
  onSelectTrip,
  isUserJoined,
  locationFilter = '',
}) => {
  const compact = dayCount > 7;
  const days = useMemo(() => buildDayRange(dayCount), [dayCount]);

  const filteredTrips = useMemo(() => {
    if (!locationFilter.trim()) return trips;
    const q = locationFilter.toLowerCase();
    return trips.filter(
      (t) =>
        t.pickup_location?.toLowerCase().includes(q) ||
        t.dropoff_location?.toLowerCase().includes(q)
    );
  }, [trips, locationFilter]);

  const filteredByDate = useMemo(() => groupTripsByDate(filteredTrips), [filteredTrips]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-stone-500">
          Tap a trip to see details and join.
        </p>
        <div className="flex items-center gap-3 text-xs text-stone-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Nairobi
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-sky-500" /> Arusha
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> Moshi
          </span>
        </div>
      </div>

      <div
        className={
          compact
            ? 'flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0'
            : 'flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin -mx-4 px-4 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-7 lg:overflow-visible lg:snap-none lg:pb-0'
        }
      >
        {days.map((date) => {
          const key = toDateKey(date);
          const dayTrips = filteredByDate[key] || [];
          return (
            <div
              key={key}
              className={
                compact
                  ? 'snap-start flex-shrink-0 w-[100px]'
                  : 'snap-center flex-shrink-0 w-[min(82vw,280px)] sm:w-[min(48vw,260px)] md:w-[min(36vw,240px)] lg:w-auto lg:min-w-0'
              }
            >
              <DayColumn
                date={date}
                trips={dayTrips}
                compact={compact}
                selectedTripId={selectedTripId}
                onSelectTrip={onSelectTrip}
                isUserJoined={isUserJoined}
              />
            </div>
          );
        })}
      </div>

      {filteredTrips.length === 0 && (
        <p className="text-center py-8 text-stone-500 text-sm">
          No trips in the next {dayCount} days matching your filters.
        </p>
      )}
    </div>
  );
};

export default TripScheduleView;
